import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { MetaGamesEventType } from '../../dto/metagames.types';
import type { MetagamesCurrentEvent, MetagamesResponse } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { auth } from '../../services/auth';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { hasEndedCardWarInstance, markCardWarInstanceEnded } from '../../utils/cardWarEndedTracker';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import { addResourceHint } from '../../utils/resourceHints';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

/**
 * game-ui mounts the Card War arena as a second, cross-origin Rive iframe only after its event fetch
 * returns, so its cold connection lands on the open. Warming it from the lobby moves that off the click.
 */
function warmRiveArena() {
  const riveOrigin = import.meta.env.VITE_RIVE_IFRAME_URL;
  // No crossorigin: the iframe navigation and its same-origin module script are credentialed, and an
  // anonymous preconnect opens a socket Chrome won't reuse for credentialed requests.
  addResourceHint('preconnect', riveOrigin);
  // Only reused when it matches game-ui's arena src exactly.
  addResourceHint('prefetch', riveOrigin && `${riveOrigin}/?v=3&r=2`);
  // The arena's rive.wasm is a CORS fetch, so this one does need crossorigin.
  addResourceHint('preconnect', 'https://unpkg.com', 'anonymous');
}

export class CardWarStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('card_war_enabled' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.resolveAndUpdateFlags();
      } else {
        this.setCardWarFlag(false);
      }
    });
  }

  private resolveAndUpdateFlags() {
    const cardWarFlag = resolveFlag('card_war_enabled', true);

    this.setCardWarFlag(cardWarFlag);
  }

  @observable
  public cardWarFlag = false;

  @action
  public setCardWarFlag(flag: boolean) {
    this.cardWarFlag = flag;
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public cardWarEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCardWarEvent(event: MetagamesCurrentEvent | undefined) {
    this.cardWarEvent = event;
    if (event) {
      warmRiveArena();
    }
  }

  @observable
  public currentOpenCardWarEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenCardWarEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenCardWarEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  @action
  private updateOptInStatus() {
    if (!this.cardWarEvent) {
      return;
    }

    const metaCoinType = this.rootStore.coinTypeStore.coinType.toLocaleLowerCase() as 'gc' | 'sc';
    const current = this.cardWarEvent[metaCoinType];

    this.setCardWarEvent({
      ...this.cardWarEvent,
      [metaCoinType]: { ...current, isOptedIn: true },
    });

    if (
      this.currentOpenCardWarEvent &&
      this.currentOpenCardWarEvent.id === this.cardWarEvent.id &&
      this.currentOpenCardWarEvent[metaCoinType]
    ) {
      this.setCurrentOpenCardWarEvent({
        ...this.currentOpenCardWarEvent,
        [metaCoinType]: { ...this.currentOpenCardWarEvent[metaCoinType], isOptedIn: true },
      });
    }
  }

  public async acknowledgeOptIn() {
    try {
      const { coinType } = this.rootStore.coinTypeStore;
      const eventId = this.cardWarEvent?.id;
      void this.updateOptInStatus();
      await postRequest<{ data: MetagamesResponse }>('metagame-events/opt-in', { coinType, eventId });
    } catch (error) {
      devError('CardWarStore: acknowledgeOptIn error', error);
    }
  }

  /**
   * Bumped when an instance is ended, so the widget goes the moment the player quits rather than on
   * the next fetch. The answer itself is read from storage — this only makes it observable.
   */
  @observable
  private endedTick = 0;

  /** The player tapped x on Do-It-Again: this instance is over for them, on this coin only. */
  @action
  public markCurrentEventEnded() {
    const eventId = this.cardWarEvent?.id;
    const userId = auth.userId;

    if (!eventId || !userId) {
      return;
    }

    markCardWarInstanceEnded(userId, eventId, this.rootStore.coinTypeStore.coinType);
    this.endedTick += 1;
  }

  /** Whether the live event is one the player has already ended on the coin they are looking at. */
  @computed
  public get isCurrentEventEnded() {
    const eventId = this.cardWarEvent?.id;
    const userId = auth.userId;

    // read so mobx re-evaluates on markCurrentEventEnded; localStorage is not observable
    void this.endedTick;

    return Boolean(
      eventId && userId && hasEndedCardWarInstance(userId, eventId, this.rootStore.coinTypeStore.coinType),
    );
  }

  @computed
  public get eventByCoin() {
    const eventToUse = this.currentOpenCardWarEvent || this.cardWarEvent;
    if (!eventToUse) {
      return undefined;
    }
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;
    return eventToUse[currentCoinType.toLowerCase() as 'gc' | 'sc'];
  }

  public startDialogs(type?: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (
      this.eventByCoin &&
      type &&
      ['CardWar', 'CardWarPreMessage'].includes(type) &&
      !this.eventByCoin.isOptedIn &&
      !this.isCurrentEventEnded
    ) {
      void this.startCardWarGame();
      return true;
    }

    return false;
  }

  public async startCardWarGame() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      if (!this.cardWarEvent) {
        devError('CardWarStore: startCardWarGame error: card war event not found');
        return;
      }

      const eventByCoin = this.cardWarEvent[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('CardWarStore: startCardWarGame error: event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('CardWarStore: startCardWarGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (!metagames) {
        devError('CardWarStore: startCardWarGame error: metagames not found');
        this.closeGame();
        return;
      }

      const { token, path, accessToken } = metagames;
      const { gameUiFlag } = this.rootStore.gameStore;

      const metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
        version: gameUiFlag ? '2' : '1',
        metagamesToken: gameUiFlag ? token : undefined,
        metagamesAccessToken: gameUiFlag ? accessToken : undefined,
        monetizationEventsToken: gameUiFlag ? token : undefined,
        token: gameUiFlag ? undefined : token,
        coinType: currentCoinType,
        flags: JSON.stringify([{ name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) }]),
      });

      this.setCurrentOpenCardWarEvent({
        ...this.cardWarEvent,
        token,
        path,
        url: metagamesUrl,
        startDate: this.cardWarEvent.startDate,
        endDate: this.cardWarEvent.endDate,
      });

      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenMetagamesDialog,
        options: { type: MetaGamesEventType.CARD_WAR },
      });
    } catch (error) {
      devError('CardWarStore: startCardWarGame error', getErrorMessage(error));
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame() {
    this.setCurrentOpenCardWarEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    this.rootStore.featureManagerStore.onDialogFinished('CardWarPreMessage');
  }

  @action
  public reset() {
    this.cardWarEvent = undefined;
    this.currentOpenCardWarEvent = undefined;
    this.isLoadingGame = false;
  }

  public sendAnalyticsEvent(analyticsParams: {
    event_action: 'click' | 'impression';
    triggered_by?: 'click' | 'lobby';
    click_location?: 'widget' | 'in_game_widget' | 'close';
  }) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      click_location: analyticsParams.click_location ?? 'widget',
      feature_sub_type: 'cardWar',
    });
  }
}
